<template>
  <div :class="toolbarStyles" class="text-xs border">
    <button
      class="btn btn-sm btn-square tooltip"
      :class="{ 'btn-primary': selectedTool === 'brush' }"
      @click="handleToolSelect('brush')"
      title="画笔 (B)"
      data-tip="画笔"
    >
      <i class="iconify mdi--brush"></i>
    </button>
    <button
      class="btn btn-sm btn-square tooltip"
      :class="{ 'btn-primary': selectedTool === 'eraser' }"
      @click="handleToolSelect('eraser')"
      title="橡皮 (E)"
      data-tip="橡皮"
    >
      <i class="iconify mdi--eraser"></i>
    </button>
    <button
      class="btn btn-sm btn-square tooltip"
      :class="{ 'btn-primary': selectedTool === 'fill' }"
      @click="handleToolSelect('fill')"
      title="填充 (G)"
      data-tip="填充"
    >
      <i class="iconify mdi--format-color-fill"></i>
    </button>
    <button
      class="btn btn-sm btn-square tooltip"
      :class="{ 'btn-primary': selectedTool === 'eyedropper' }"
      @click="handleToolSelect('eyedropper')"
      title="Color Picker (I)"
      data-tip="颜色提取"
    >
      <i class="iconify mdi--eyedropper"></i>
    </button>
    <div class="divider my-1"></div>

    <!-- Brush Size Button -->
    <button
      class="btn btn-sm btn-square tooltip"
      @click="showBrushDialog = true"
      :class="{
        'btn-primary': selectedTool === 'brush' || selectedTool === 'eraser',
      }"
      title="画笔大小"
      data-tip="画笔大小"
    >
      <div class="flex items-center justify-center">
        <div
          class="w-2 h-2 rounded-full bg-current"
          :style="{ transform: `scale(${localBrushSize / 2})` }"
        ></div>
      </div>
    </button>

    <div class="divider my-1"></div>
    <div
      :class="colorPickerContainerStyles"
      class="rounded-full border overflow-hidden shadow"
    >
      <input
        type="color"
        v-model="localColor"
        class="w-8 h-8 cursor-pointer"
        @input="$emit('update:color', localColor)"
      />
    </div>
    <div class="divider my-1"></div>
    <button
      class="btn btn-sm btn-square tooltip"
      @click="$emit('undo')"
      :disabled="!canUndo"
      title="撤销 (⌘Z)"
      data-tip="撤销"
    >
      <i class="iconify mdi--undo"></i>
    </button>
    <button
      class="btn btn-sm btn-square tooltip"
      @click="$emit('redo')"
      :disabled="!canRedo"
      title="重做 (⇧⌘Z)"
      data-tip="重做"
    >
      <i class="iconify mdi--redo"></i>
    </button>

    <!-- Brush Size Dialog -->
    <BrushSizeDialog
      v-if="showBrushDialog"
      :size="localBrushSize"
      @update:size="handleBrushSizeUpdate"
      @close="showBrushDialog = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import type { Tool } from "@/features/draw/types";
import BrushSizeDialog from "./dialogs/BrushSizeDialog.vue";
import { useBreakpoint } from "@/features/draw/composables/useBreakpoint";
import { useUIControls } from "@/features/draw/composables/useUIControls";

// 使用断点composable
const { breakpointSwitch } = useBreakpoint();

// Props definition
const props = defineProps<{
  selectedTool: Tool;
  canUndo: boolean;
  canRedo: boolean;
  brushSize: number;
  color: string;
}>();

// Emits definition
const emit = defineEmits([
  "update:tool",
  "update:brushSize",
  "update:color",
  "undo",
  "redo",
]);

// Local state for two-way binding
const localBrushSize = ref(props.brushSize);
const localColor = ref(props.color);
const showBrushDialog = ref(false);
const { toolbarStyles } = useUIControls();

// 颜色选择器容器样式
const colorPickerContainerStyles = computed(() => {
  const baseStyles = "flex items-center gap-2";

  return breakpointSwitch({
    xl: () => `${baseStyles} flex-col`,
    "2xl": () => `${baseStyles} flex-col`,
    default: () => baseStyles,
  });
});

// Watch for prop changes
watch(
  () => props.brushSize,
  (newValue) => {
    localBrushSize.value = newValue;
  }
);

watch(
  () => props.color,
  (newValue) => {
    localColor.value = newValue;
  }
);

// Event handlers
const handleToolSelect = (tool: Tool) => {
  emit("update:tool", tool);
};

const handleBrushSizeUpdate = (size: number) => {
  localBrushSize.value = size;
  emit("update:brushSize", size);
};
</script>

<style scoped>
/* DaisyUI classes are now handling all the styling */
input[type="color"] {
  -webkit-appearance: none;
  border: none;
}

input[type="color"]::-webkit-color-swatch-wrapper {
  padding: 0;
}

input[type="color"]::-webkit-color-swatch {
  border: none;
  border-radius: 4px;
}
</style>
